import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { MESSAGE_FORMAT } from "../../Types/account";
import { isMarkdownFormat } from "../../utils/socialPromptMessageHelpers";
import { SocialPromptMessagePreview } from "./SocialPromptMessagePreview";
import "../../Css/Common/SocialPromptMessage.css";

type EditorMode = "edit" | "preview";

export type SocialPromptMessageEditorProps = {
  message: string;
  messageFormat: MESSAGE_FORMAT;
  onChange: (value: string) => void;
  id?: string;
  ariaLabel?: string;
  className?: string;
  textareaClassName?: string;
  rows?: number;
  autoGrow?: boolean;
  /**
   * Optional: render Preview/Edit controls outside the editor body (e.g. label row).
   * Called with `null` for TEXT format so the surrounding chrome can still render.
   */
  renderToolbar?: (toolbar: React.ReactNode) => React.ReactNode;
};

export const SocialPromptMessageEditor: React.FC<SocialPromptMessageEditorProps> = ({
  message,
  messageFormat,
  onChange,
  id,
  ariaLabel = "Prompt message",
  className,
  textareaClassName,
  rows,
  autoGrow = false,
  renderToolbar,
}) => {
  const markdown = isMarkdownFormat(messageFormat);
  const [mode, setMode] = useState<EditorMode>(markdown ? "preview" : "edit");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setMode(isMarkdownFormat(messageFormat) ? "preview" : "edit");
  }, [messageFormat, id]);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!autoGrow || !textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [autoGrow, message, mode]);

  const toolbar = markdown ? (
    <div className="socialPromptMessage_editorToggle" role="tablist" aria-label="Message view">
      <button
        type="button"
        role="tab"
        aria-selected={mode === "preview"}
        className={`socialPromptMessage_editorToggleBtn${
          mode === "preview" ? " socialPromptMessage_editorToggleBtn--active" : ""
        }`}
        onClick={() => setMode("preview")}
      >
        Preview
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "edit"}
        className={`socialPromptMessage_editorToggleBtn${
          mode === "edit" ? " socialPromptMessage_editorToggleBtn--active" : ""
        }`}
        onClick={() => setMode("edit")}
      >
        Edit
      </button>
    </div>
  ) : null;

  const textarea = (
    <textarea
      ref={textareaRef}
      id={id}
      className={`socialPromptMessage_editorTextarea${
        textareaClassName ? ` ${textareaClassName}` : ""
      }${autoGrow ? " socialPromptMessage_editorTextarea--autoGrow" : ""}`}
      value={message}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      rows={rows}
    />
  );

  const body =
    !markdown || mode === "edit" ? (
      textarea
    ) : (
      <div className="socialPromptMessage_previewPane">
        <SocialPromptMessagePreview message={message} messageFormat={messageFormat} />
      </div>
    );

  // Plain TEXT without external toolbar: keep previous single-textarea API.
  if (!markdown && !renderToolbar) {
    return (
      <textarea
        ref={textareaRef}
        id={id}
        className={`${textareaClassName ?? className ?? ""}${
          autoGrow ? " socialPromptMessage_editorTextarea--autoGrow" : ""
        }`.trim()}
        value={message}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        rows={rows}
      />
    );
  }

  return (
    <>
      {renderToolbar ? renderToolbar(toolbar) : null}
      <div className={`socialPromptMessage_editor${className ? ` ${className}` : ""}`}>
        {renderToolbar || !markdown ? null : toolbar}
        {body}
      </div>
    </>
  );
};
